import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Headline from "~/components/Headline/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateStaffMembersBankAccount } from "~/modules/humanResources/api/staffMember/staffMemberApiDispatchers.ts";
import type { StaffMember } from "~/modules/humanResources/api/staffMember/staffMemberTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type UpdateBankAccountFormData = {
	bankName: string | null;
	bankAccountOwner: string | null;
	bic: string | null;
	iban: string | null;
}

type UpdateBankAccountFormProps = {
	onSuccess: () => void;
	onCancel: () => void;
	staffMemberCoreData: StaffMember;
};

const UpdateBankAccountForm: React.FunctionComponent<UpdateBankAccountFormProps> = ({
	onSuccess,
	onCancel,
	staffMemberCoreData,
}) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			bankName: yup.string().nullable().default(null),
			bankAccountOwner: yup.string().nullable().default(null),
			bic: yup.string().nullable().default(null),
			iban: yup.string().nullable().default(null),
		});

	}, []);
	const defaultValues = useMemo(() => {
		if (staffMemberCoreData) {
			return {
				bankName: staffMemberCoreData.bankName,
				bankAccountOwner: staffMemberCoreData.bankAccountOwner,
				bic: staffMemberCoreData.bic,
				iban: staffMemberCoreData.iban,
			};
		}
		return {
			bankName: null,
			bankAccountOwner: null,
			bic: null,
			iban: null,
		};
	}, [staffMemberCoreData]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<UpdateBankAccountFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateBankAccountFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: UpdateBankAccountFormData) => {

		try {
			setIsBusy(true);
			await updateStaffMembersBankAccount(staffMemberCoreData.id, data);
			onSuccess();
		} catch (error) {
			const apiError = handleApiError(error);
			console.log(apiError);
			setServerErrorMessage("Ein unerwarteter Fehler ist aufgetreten.");
		}
	}, [onSuccess, staffMemberCoreData.id]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<Headline type="h4"
						  color="muted">
					Bankverbindung
				</Headline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}

				<div className="grid grid-cols-2 gap-4">
					<div className="col-span-2">
						<Input control={control}
							   name="bankName"
							   label="Name der Bank"
							   placeholder="Hamburger Sparkasse"
						/>
					</div>
					<div className="col-span-2">
						<Input control={control}
							   name="bankAccountOwner"
							   label="Kontoinhaber/in"
							   placeholder="Maria Mustermann"
						/>
					</div>
					<Input control={control}
						   name="iban"
						   label="IBAN"
						   placeholder="DEXX XXXX XXXX XXXX XX"
					/>
					<Input control={control}
						   name="bic"
						   label="BIC"
						   placeholder="HASPDEHHXXX"
					/>
				</div>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateBankAccountForm;
